/**
 * MentionInputBackdrop — renders @-mention markers as citation chips INSIDE the
 * composer.
 *
 * A <textarea> can only hold plain text, so a picked attachment lives in the
 * value as the marker ``@[1]``. To show it as a ``[1]`` citation chip without
 * giving up the native textarea (caret, selection, IME), we paint a read-only
 * layer exactly behind a transparent-text textarea: this backdrop renders the
 * SAME characters styled, and the textarea on top supplies the caret.
 *
 * Alignment is everything — the backdrop must not change any glyph's advance
 * width, or the visible text drifts from the real caret. So the chip span adds
 * NO horizontal padding/margin/letter-spacing and varies COLOUR only; the
 * leading ``@`` is kept (it still occupies width) but painted transparent so
 * only the ``[n]`` shows, reading as the chip's left padding.
 *
 * The marker numbers are kept contiguous by ../mentions (renumberMentions).
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { findMentionTokens } from "../mentions";
import { brandColors, inkColors, surfaceColors } from "../tokens";

// Computed-style properties copied from the textarea so the backdrop's text
// wraps and advances identically. Border/padding are mirrored (border painted
// transparent) so the content box lines up with the textarea's.
const MIRRORED_PROPS = [
  "boxSizing",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "letterSpacing",
  "lineHeight",
  "textTransform",
  "wordSpacing",
  "tabSize",
] as const;

const useStyles = makeStyles({
  // Static clip viewport, pinned to the textarea's box. It must NOT be
  // transformed: the scroll offset lives on the inner content layer, so this
  // element's overflow-clip box stays put and always clips to the visible box.
  root: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    pointerEvents: "none",
  },
  // The painted text/pill layer. It is transformed to mirror the textarea's
  // internal scroll (translate(-scrollTop)); because it is nested inside the
  // static `root`, translating it scrolls the content WITHIN the fixed clip
  // window instead of dragging the clip window along with it.
  content: {
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
    color: inkColors.ink,
  },
  // The "[n]" citation chip. Brand accent + a crisp inset hairline; the
  // "@" before it is painted transparent so it reads as ~1ch of left padding.
  // Colour only — never size/weight/letter-spacing, which would desync the
  // advance width from the textarea and drift the caret.
  pill: {
    borderRadius: "4px",
    background: brandColors.light,
    color: brandColors.primary,
    boxShadow: `inset 0 0 0 1px ${surfaceColors.lineStrong}`,
  },
  // The leading "@" only: present for glyph width (keeps the caret aligned)
  // but invisible, so it reads as the chip's left padding, not an "@".
  delim: {
    color: "transparent",
  },
});

export interface MentionInputBackdropProps {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  value: string;
}

export const MentionInputBackdrop: React.FC<MentionInputBackdropProps> = ({ textareaRef, value }) => {
  const styles = useStyles();
  const [metrics, setMetrics] = useState<React.CSSProperties>({});
  const [scroll, setScroll] = useState({ top: 0, left: 0 });

  // Copy the textarea's box metrics; re-copy on resize (auto-grow, pane resize).
  useLayoutEffect(() => {
    const el = textareaRef.current;
    const win = el?.ownerDocument.defaultView;
    if (!el || !win) return;
    const sync = (): void => {
      const c = win.getComputedStyle(el);
      const next: Record<string, string> = {};
      for (const prop of MIRRORED_PROPS) next[prop] = c[prop];
      setMetrics(next as React.CSSProperties);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [textareaRef]);

  // Follow the textarea's scroll so long content stays aligned. Re-syncing on
  // `value` too is deliberate: the auto-grow hook changes the textarea height
  // programmatically, which shifts scrollTop without emitting a `scroll` event,
  // so the offset would otherwise lag right when the height cap is first hit.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const onScroll = (): void => setScroll({ top: el.scrollTop, left: el.scrollLeft });
    el.addEventListener("scroll", onScroll);
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [textareaRef, value]);

  const segments: React.ReactNode[] = [];
  const tokens = findMentionTokens(value);
  let cursor = 0;
  tokens.forEach((token, i) => {
    if (token.start > cursor) segments.push(value.slice(cursor, token.start));
    // The rendered "@" + "[n]" concatenate to exactly the token text "@[n]",
    // so the chip's advance width equals the textarea's (caret stays aligned);
    // the "@" is painted transparent so only the "[n]" citation shows.
    segments.push(
      <span key={`pill-${i}-${token.start}`} className={styles.pill} data-testid="mention-pill">
        <span className={styles.delim}>@</span>
        {`[${token.number}]`}
      </span>,
    );
    cursor = token.end;
  });
  if (cursor < value.length) segments.push(value.slice(cursor));

  return (
    <div aria-hidden="true" className={mergeClasses(styles.root)} data-testid="mention-backdrop">
      <div
        className={mergeClasses(styles.content)}
        data-testid="mention-backdrop-content"
        // Border painted transparent (inline, so Griffel's shorthand ban doesn't
        // apply) so the mirrored border widths still offset the content box.
        style={{
          ...metrics,
          borderStyle: "solid",
          borderColor: "transparent",
          transform: `translate(${-scroll.left}px, ${-scroll.top}px)`,
        }}
      >
        {segments}
      </div>
    </div>
  );
};

export default MentionInputBackdrop;
